// GL6 §4.9 — bağlantı çağrılarının hata metni. `ACTIVATION_LINK_COOLDOWN`'ın backend metni yer tutucusuzdur
// (yedek); saat satırı web'e özel anahtardadır: `messageData.availableAt` (UTC ISO) yerel saate çevrilir,
// bir SONRAKİ tam dakikaya YUKARI yuvarlanır (60 sn bekleme çoğu kez aynı dakikaya düşer; aşağı yuvarlama
// "saat 14:31" deyip 14:31'de yine 429 alırdı) ve `{{availableAt}}` olarak verilir. Çözülemezse ham ISO
// ekrana ASLA girmez: `t('ACTIVATION_LINK_COOLDOWN')`. Diğer her kod `resolveApiErrorMessage`'a.
// Create bağlamında (`create: true`) iki 429 da iletişime dair sayfa-yerel anahtarlarla söylenir: reddedilen
// kişi değil iletişimdir ve iletişimsiz oluşturmak iki kontrolü de atlar (contract §4.2 adım 7).
import type { TFunction } from 'i18next';
import { isApiError } from '../api/errors';
import { resolveApiErrorMessage } from '../i18n/apiError';
import { currentCulture } from '../i18n/i18n';

export interface ActivationLinkErrorOptions {
  create?: boolean;
  fallbackKey?: string;
}

/** Bağlantı gönderim sınırları (bekleme / günlük tavan): sebep iletişim, çare iletişimsiz oluşturmak. */
export function isLinkRateLimit(error: unknown): boolean {
  return (
    isApiError(error) &&
    (error.errorCode === 'ACTIVATION_LINK_COOLDOWN' ||
      error.errorCode === 'ACTIVATION_LINK_DAILY_LIMIT')
  );
}

function clockOf(data: Record<string, unknown> | null): string | null {
  const raw = data?.availableAt;
  if (typeof raw !== 'string') {
    return null;
  }
  const at = new Date(raw);
  if (Number.isNaN(at.getTime())) {
    return null;
  }
  const rounded = new Date(Math.ceil(at.getTime() / 60_000) * 60_000);
  try {
    return new Intl.DateTimeFormat(currentCulture(), {
      hour: '2-digit',
      minute: '2-digit',
    }).format(rounded);
  } catch {
    return null;
  }
}

/**
 * Bağlantı bayat: backend o uuid'i artık tanımıyor. `INVITATION_NOT_FOUND` kişi bağlantısında yanlış isim
 * ("davet") söyler; firma yüzeylerinde (`PersonnelLinkModal`, iptal onayı) tek nötr metne gider.
 * `GUEST_INVITE_INVALID` YALNIZ katılan kişiye ait bir koddur ve hiçbir firma yüzeyine ulaşmaz;
 * `/guest/activate` kendi metnini korur.
 */
export function isStaleLinkError(error: unknown): boolean {
  return isApiError(error) && error.errorCode === 'INVITATION_NOT_FOUND';
}

export function resolveActivationLinkError(
  error: unknown,
  t: TFunction,
  options: ActivationLinkErrorOptions = {},
): string {
  if (isStaleLinkError(error)) {
    return t('personnelLink.notFound');
  }
  if (isApiError(error)) {
    // D1: bu kodun metni "başka bir hesapta kayıtlı" der; hiçbir firma yüzeyinde `t(code)` ile gösterilmez.
    if (error.errorCode === 'GUEST_INVITE_CONTACT_ALREADY_EXISTS') {
      return t('personnelLink.validation.contactTaken');
    }
    if (error.errorCode === 'ACTIVATION_LINK_COOLDOWN') {
      const clock = clockOf(error.messageData);
      if (options.create) {
        return clock === null
          ? t('personnelCreate.cooldownGeneric')
          : t('personnelCreate.cooldownUntil', { availableAt: clock });
      }
      return clock === null
        ? t('ACTIVATION_LINK_COOLDOWN')
        : t('personnelLink.cooldownUntil', { availableAt: clock });
    }
    if (error.errorCode === 'ACTIVATION_LINK_DAILY_LIMIT' && options.create) {
      return t('personnelCreate.dailyLimit');
    }
  }
  return resolveApiErrorMessage(error, t, options.fallbackKey);
}
